import React, { useEffect } from 'react';
import { Box, Typography, Paper, useTheme, Fade } from '@mui/material';
import AOS from 'aos';
import 'aos/dist/aos.css';

/* Team section - commented out for later use
const team = [
  {
    name: 'Pradeep Sain',
    role: 'Founder & CEO',
    avatar: '/PradeepSain.png',
    bio: 'Visionary leader passionate about democratizing software creation through AI.',
    linkedin: '#',
  },
  {
    name: 'Sourabh Panjwani',
    role: 'Lead Software Architect',
    avatar: '/SourabhPanjwani.png',
    bio: 'An architect and full-stack engineer, building the brains behind Bixbite AI.',
    linkedin: '#',
  },
  {
    name: 'Mayank Sharma',
    role: 'Lead Software Architect',
    avatar: '/MayankSharma.png',
    bio: 'Lead Software Architect driving scalable, robust, and innovative software solutions.',
    linkedin: '#',
  },
  {
    name: 'Abhilash N',
    role: 'Lead Software Architect',
    avatar: '/AbhilashN.png',
    bio: 'Senior Technology Strategist delivering high-performance, future-ready, and impactful software products.',
    linkedin: '#',
  },
];
*/

const AboutUs = () => {
  const theme = useTheme();

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: 'ease-out-cubic',
      disable: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    });
  }, []);

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: '28px',
          p: { xs: 4, md: 8 },
          border: '1px solid rgba(0, 121, 107, 0.1)',
          bgcolor: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background Decorations */}
        <Box
          sx={{
            position: 'absolute',
            top: -80,
            left: -80,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'rgba(0, 121, 107, 0.05)',
            filter: 'blur(60px)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -60,
            right: -60,
            width: 250,
            height: 250,
            borderRadius: '50%',
            background: 'rgba(0, 77, 64, 0.04)',
            filter: 'blur(80px)',
            pointerEvents: 'none',
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          {/* Section Header */}
          <Fade in timeout={600}>
            <Box sx={{ textAlign: 'center', mb: 6 }} data-aos="fade-up">
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  background: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                About Bixbite AI
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.text.secondary,
                  maxWidth: 800,
                  mx: 'auto',
                  fontWeight: 400,
                  lineHeight: 1.7,
                  mb: 3,
                }}
              >
                Bixbite AI is an AI-powered software development platform that empowers anyone to build custom software applications by simply chatting with an AI agent. Our mission is to make software creation accessible, intuitive, and lightning-fast—no coding experience required.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.secondary,
                  maxWidth: 700,
                  mx: 'auto',
                  lineHeight: 1.8,
                }}
              >
                We believe in a world where technology is a tool for everyone. By harnessing the power of advanced natural language processing and code generation, Bixbite AI bridges the gap between ideas and reality, enabling entrepreneurs, businesses, and creators to bring their visions to life effortlessly.
              </Typography>
            </Box>
          </Fade>

          {/* Team Section - commented out for later use
          <Typography
            variant="h4"
            align="center"
            sx={{
              fontWeight: 700,
              mb: 5,
              color: '#004d40',
            }}
            data-aos="fade-up"
          >
            Meet Our Team
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {team.map((member, idx) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Fade in timeout={400 + idx * 100}>
                  <Paper
                    elevation={0}
                    onMouseEnter={() => setHoveredMember(idx)}
                    onMouseLeave={() => setHoveredMember(null)}
                    sx={{
                      p: 4,
                      borderRadius: '24px',
                      textAlign: 'center',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      border: '1px solid rgba(0, 121, 107, 0.1)',
                      background: 'linear-gradient(180deg, #ffffff 0%, #f8fffe 100%)',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                      transform: hoveredMember === idx ? 'translateY(-10px)' : 'translateY(0)',
                      boxShadow: hoveredMember === idx 
                        ? '0 25px 50px -15px rgba(0, 77, 64, 0.25)' 
                        : '0 4px 20px -8px rgba(0, 77, 64, 0.08)',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: 'linear-gradient(90deg, #00796b, #004d40)',
                        transform: hoveredMember === idx ? 'scaleX(1)' : 'scaleX(0)',
                        transformOrigin: 'left',
                        transition: 'transform 0.4s ease',
                      },
                    }}
                    data-aos="zoom-in"
                    data-aos-delay={100 * idx}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        mb: 3,
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: -6,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, rgba(0, 121, 107, 0.2) 0%, rgba(0, 77, 64, 0.15) 100%)',
                          opacity: hoveredMember === idx ? 1 : 0,
                          transition: 'opacity 0.3s ease',
                          animation: hoveredMember === idx ? 'pulse 2s ease-in-out infinite' : 'none',
                        }}
                      />
                      <Avatar
                        src={member.avatar}
                        alt={member.name}
                        sx={{
                          width: 100,
                          height: 100,
                          border: '4px solid',
                          borderColor: hoveredMember === idx ? '#00796b' : 'rgba(0, 121, 107, 0.15)',
                          boxShadow: hoveredMember === idx 
                            ? '0 12px 30px -8px rgba(0, 77, 64, 0.35)' 
                            : '0 8px 20px -8px rgba(0, 77, 64, 0.2)',
                          transition: 'all 0.3s ease',
                          transform: hoveredMember === idx ? 'scale(1.05)' : 'scale(1)',
                        }}
                      />
                    </Box>

                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700, 
                        mb: 0.5, 
                        color: '#1a2f2c',
                      }}
                    >
                      {member.name}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ 
                        background: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        fontWeight: 600, 
                        mb: 2,
                      }}
                    >
                      {member.role}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ 
                        color: theme.palette.text.secondary, 
                        lineHeight: 1.6,
                        fontSize: '0.9rem',
                        flex: 1,
                      }}
                    >
                      {member.bio}
                    </Typography>

                    <Box
                      sx={{
                        mt: 3,
                        width: 40,
                        height: 40,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: hoveredMember === idx ? '#00796b' : 'rgba(0, 121, 107, 0.08)',
                        color: hoveredMember === idx ? 'white' : '#00796b',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: '#00796b',
                          color: 'white',
                          transform: 'scale(1.1)',
                        },
                      }}
                    >
                      <LinkedInIcon sx={{ fontSize: 20 }} />
                    </Box>
                  </Paper>
                </Fade>
              </Grid>
            ))}
          </Grid>
          */}
        </Box>
      </Paper>
    </Box>
  );
};

export default AboutUs;
